import React, { Suspense, lazy, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { hasPermission } from '@lognai/plugin-sdk';
import { Tabs } from '@lognai/synapse-design-system-v2';

/**
 * Both modes render at exactly this height so the frame never jumps on toggle.
 * Matches `RadarMap`'s own DEFAULT_HEIGHT, so the dashboard tile and the full
 * /shipments/radar page agree on one number rather than drifting apart.
 */
const MAP_HEIGHT = 560;

const PERMISSION = {
  shipment: 'shipment:shipment:read',
  global: 'global_insights:news:read',
} as const;

const LABEL = {
  shipment: 'Shipment Radar',
  global: 'Global Radar',
} as const;

type RadarMode = keyof typeof PERMISSION;

// Chunk boundaries. The unselected mode's code is never downloaded — the old
// tabs statically imported GlobalInsightsPanel, so Leaflet and the risk map
// shipped to every user, including those who could never open the tab.
const ShipmentRadarTile = lazy(() => import('./ShipmentRadarTile'));
const GlobalRadarTile = lazy(() => import('./GlobalRadarTile'));

const TileSkeleton: React.FC<{ height: number }> = ({ height }) => (
  <div className="animate-pulse bg-muted" style={{ height }} />
);

export interface RadarSectionProps {
  height?: number;
  embedded?: boolean;
  className?: string;
}

/**
 * The dashboard's situational-awareness frame: one map, two layers.
 *
 * The inactive tile is unmounted rather than hidden. This is a correctness
 * requirement: Leaflet measures zero inside a display:none container and paints
 * a permanent grey box. GlobalRiskMap carries a MapInvalidator to survive that;
 * RadarMap has no such defence. Conditional rendering means every map mounts
 * into an already-visible, correctly-sized box. React Query caches both
 * datasets, so a toggle costs a Leaflet re-init, not a refetch.
 */
export const RadarSection: React.FC<RadarSectionProps> = ({
  height = MAP_HEIGHT,
  embedded = false,
  className = '',
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const available = useMemo(
    () => (['shipment', 'global'] as RadarMode[]).filter((m) => hasPermission(PERMISSION[m])),
    []
  );

  // `?tab=global` is the retired tab param. Honour inbound links, never re-emit it.
  const requested: RadarMode | null =
    searchParams.get('radar') === 'global' || searchParams.get('tab') === 'global'
      ? 'global'
      : searchParams.get('radar') === 'shipment'
        ? 'shipment'
        : null;

  if (available.length === 0) return null;

  // A toggle must never offer a mode that would render a permission error.
  const mode: RadarMode =
    requested && available.includes(requested) ? requested : (available[0] as RadarMode);

  const selectMode = (next: RadarMode) => {
    const params = new URLSearchParams(searchParams);
    params.delete('tab');
    if (next === 'shipment') params.delete('radar');
    else params.set('radar', next);
    setSearchParams(params, { replace: true });
  };

  return (
    <section
      className={`flex min-h-0 flex-col overflow-hidden bg-surface ${
        embedded ? '' : 'rounded-xl border border-border shadow-sm'
      } ${className}`}
    >
      <Tabs
        value={mode}
        onValueChange={(value) => selectMode(value as RadarMode)}
        className="flex min-h-0 flex-1 flex-col"
      >
        <div className="flex min-h-11 shrink-0 items-end justify-between gap-3 border-b border-border px-4">
          <Tabs.List aria-label="Radar layer" className="border-b-0">
            {available.map((m) => (
              <Tabs.Trigger
                key={m}
                value={m}
                className="px-1.5 pb-3 pt-2 sm:px-3"
              >
                {LABEL[m]}
              </Tabs.Trigger>
            ))}
          </Tabs.List>

          {mode === 'shipment' && (
            <Link
              to="/shipments/radar"
              className="shrink-0 pb-3 text-sm text-primary hover:underline"
            >
              View full radar →
            </Link>
          )}
        </div>

        <Tabs.Content value={mode} className="m-0 min-h-0 flex-1">
          <Suspense fallback={<TileSkeleton height={height} />}>
            {mode === 'shipment' ? (
              <ShipmentRadarTile height={height} />
            ) : (
              <GlobalRadarTile height={height} />
            )}
          </Suspense>
        </Tabs.Content>
      </Tabs>
    </section>
  );
};
